import { useEffect, useState } from "react";
import { Link } from "react-scroll";

/*--------------------
* Typewriter effect
----------------------*/
function useTypewriter(words, { typeMs = 90, eraseMs = 45, holdMs = 1800 } = {}) {
  const [wordIndex, setWordIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    const word = words[wordIndex];
    let delay;
    if (!erasing && charCount === word.length) {
      delay = holdMs;
    } else {
      delay = erasing ? eraseMs : typeMs;
    }
    const timer = setTimeout(() => {
      if (!erasing) {
        if (charCount === word.length) {
          setErasing(true);
        } else {
          setCharCount(charCount + 1);
        }
      } else if (charCount === 0) {
        setErasing(false);
        setWordIndex((wordIndex + 1) % words.length);
      } else {
        setCharCount(charCount - 1);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [words, wordIndex, charCount, erasing, typeMs, eraseMs, holdMs]);

  return words[wordIndex].slice(0, charCount);
}

function Typewriter({ words }) {
  const text = useTypewriter(words);
  return (
    <span>
      {text}
      <span className="inline-block w-[3px] h-[0.85em] bg-ember-400 align-baseline translate-y-[0.12em] animate-twinkle ml-1"></span>
    </span>
  );
}

/*--------------------
* Hero Section
----------------------*/

// Deterministic star field — same sky on every render
const STARS = Array.from({ length: 90 }, (_, i) => {
  const rand = (n) => {
    const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453;
    return x - Math.floor(x);
  };
  return {
    left: rand(1) * 100,
    top: rand(2) * 60,
    size: 1 + rand(3) * 1.6,
    delay: rand(4) * 4.5,
    opacity: 0.25 + rand(5) * 0.6,
  };
});

function StarField() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {STARS.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-mist-100 animate-twinkle"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            opacity: s.opacity,
            animationDelay: `${s.delay}s`,
          }}
        ></span>
      ))}
    </div>
  );
}

function MountainRidges() {
  return (
    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" aria-hidden="true">
      {/* Dusk horizon glow behind the ridgelines */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-[380px]"
        style={{
          background:
            "radial-gradient(ellipse 60% 100% at 50% 100%, rgba(255,140,66,0.22) 0%, rgba(139,92,246,0.12) 45%, transparent 75%)",
        }}
      ></div>
      <svg
        className="relative block w-full h-[140px] md:h-[220px]"
        viewBox="0 0 1440 260"
        preserveAspectRatio="none"
      >
        <path
          d="M0,260 L0,175 L230,85 L420,190 L630,55 L850,200 L1080,105 L1290,215 L1440,145 L1440,260 Z"
          fill="#102a3b"
          opacity="0.55"
        />
        <path
          d="M0,260 L0,215 L180,135 L360,225 L560,115 L780,235 L990,150 L1210,242 L1440,175 L1440,260 Z"
          fill="#071019"
        />
      </svg>
    </div>
  );
}

export default function Banner() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-night-950 via-night-900 to-night-800 pt-[140px] pb-[200px] md:pb-[260px]"
    >
      <div className="absolute inset-0 bg-grid" aria-hidden="true"></div>
      <StarField />

      <div className="container relative z-[1]">
        <div className="max-w-[820px] mx-auto text-center">
          <p className="eyebrow mb-6">
            Twilight Tier — venture holding co.
          </p>
          <h1 className="text-[42px] md:text-[64px] lg:text-[76px] font-[700] leading-[1.05] mb-7">
            We build the{" "}
            <span className="gradient-text">
              <Typewriter
                words={["ideas", "apps", "products", "startups", "experiments"]}
              />
            </span>
            <br />
            that keep us up at night.
          </h1>
          <p className="text-[17px] md:text-[20px] text-mist-400 max-w-[560px] mx-auto mb-10 leading-relaxed">
            The home for a portfolio of software ventures — born after hours,
            built for daylight.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link className="btn btn-ember" smooth={true} to="ventures">
              Explore the ventures
            </Link>
            <Link className="btn btn-ghost" smooth={true} to="contact">
              Get in touch
            </Link>
          </div>
        </div>
      </div>

      <MountainRidges />
    </section>
  );
}
